import React, { useState } from 'react';
import CalendarControls from '../components/Calendar/CalendarControls';
import CalendarGrid from '../components/Calendar/CalendarGrid';
import SlideoutPanel from '../components/Common/SlideoutPanel';
import AddBookingForm from '../components/Forms/AddBookingForm';
import PageHeader from '../components/Common/PageHeader';

const CalendarView = ({ mockData, setMockData, showNotification }) => {
  // Local slideout state instead of useSlideout hook
  const [slideoutState, setSlideoutState] = useState({
    isOpen: false,
    title: '',
    content: null,
    width: 'medium'
  });

  const [currentDate, setCurrentDate] = useState(new Date());
  const [view, setView] = useState('month');
  const [selectedDate, setSelectedDate] = useState(null);
  const [filters, setFilters] = useState({
    practitioner: '',
    location: ''
  });

  // Mock calendar appointments data
  const [appointments, setAppointments] = useState({
    '2025-09-16': [
      { id: 'BK001', time: '09:00', patient: 'Sarah Johnson', practitioner: 'Dr. Lisa Chen', status: 'confirmed', type: 'Physiotherapy', location: 'harley-street' },
      { id: 'BK002', time: '11:30', patient: 'Michael Brown', practitioner: 'Dr. John Smith', status: 'pending', type: 'Sports Massage', location: 'harley-street' },
      { id: 'BK003', time: '14:00', patient: 'Emma Wilson', practitioner: 'Dr. Priya Patel', status: 'confirmed', type: 'Osteopathy', location: 'canary-wharf' }
    ],
    '2025-09-17': [
      { id: 'BK004', time: '10:00', patient: 'David Taylor', practitioner: 'Dr. Lisa Chen', status: 'confirmed', type: 'Acupuncture', location: 'harley-street' },
      { id: 'BK005', time: '15:30', patient: 'Jane Doe', practitioner: 'Dr. Sarah Wilson', status: 'pending', type: 'Consultation', location: 'canary-wharf' }
    ],
    '2025-09-18': [
      { id: 'BK006', time: '09:30', patient: 'Tom Smith', practitioner: 'Dr. Michael Chen', status: 'confirmed', type: 'Chiropractic', location: 'harley-street' }
    ],
    '2025-09-19': [
      { id: 'BK007', time: '11:00', patient: 'Lisa Brown', practitioner: 'Dr. Priya Patel', status: 'confirmed', type: 'Physiotherapy', location: 'canary-wharf' },
      { id: 'BK008', time: '16:00', patient: 'Mark Wilson', practitioner: 'Dr. John Smith', status: 'pending', type: 'Sports Massage', location: 'harley-street' }
    ],
    '2025-09-20': [
      { id: 'BK009', time: '13:30', patient: 'Anna Davis', practitioner: 'Dr. Sarah Wilson', status: 'confirmed', type: 'Consultation', location: 'canary-wharf' }
    ]
  });

  // Slideout functions
  const openSlideout = (title, content, width = 'medium') => {
    setSlideoutState({
      isOpen: true,
      title,
      content,
      width
    });
  };

  const closeSlideout = () => {
    setSlideoutState({
      isOpen: false,
      title: '',
      content: null,
      width: 'medium'
    });
  };

  // Handler functions
  const handleMonthChange = (direction) => {
    const newDate = new Date(currentDate);
    newDate.setMonth(currentDate.getMonth() + direction);
    setCurrentDate(newDate);
  };

  const handleViewChange = (newView) => {
    setView(newView);
    showNotification(`Switched to ${newView} view`, 'info');
  };

  const handleFiltersChange = (newFilters) => {
    setFilters(newFilters);
  };

  const handleAddAppointment = () => {
    const formContent = (
      <AddBookingForm 
        mockData={mockData}
        onSubmit={handleCreateAppointment}
        onCancel={closeSlideout}
      />
    );
    
    openSlideout('Create New Appointment', formContent, 'large');
  };

  const handleCreateAppointment = async (appointmentData) => {
    try {
      closeSlideout();
      showNotification('Appointment created successfully!', 'success');
    } catch (error) {
      showNotification('Error creating appointment. Please try again.', 'error');
    }
  };

  const handleAppointmentClick = (appointment, dateStr) => {
    // Convert appointment to booking format for the detail view
    const bookingData = {
      id: appointment.id,
      patient: appointment.patient,
      practitioner: appointment.practitioner,
      service: appointment.type,
      datetime: `${dateStr} ${appointment.time}`,
      duration: '60 min', // Default duration
      location: appointment.location === 'harley-street' ? 'Harley Street' :
                appointment.location === 'canary-wharf' ? 'Canary Wharf' :
                appointment.location === 'city-london' ? 'City of London' : 'Unknown',
      status: appointment.status
    };

    const detailContent = (
      <BookingDetail
        booking={bookingData}
        onEdit={() => handleEditAppointment(bookingData)}
      />
    );

    openSlideout(`Booking ${appointment.id}`, detailContent, 'large');
  };

  const handleEditAppointment = (bookingData) => {
    // Convert booking back to appointment format for the form
    const appointmentData = {
      patientName: bookingData.patient,
      practitioner: bookingData.practitioner,
      service: bookingData.service,
      date: bookingData.datetime.split(' ')[0],
      time: bookingData.datetime.split(' ')[1],
      location: bookingData.location
    };

    const formContent = (
      <AddBookingForm
        mockData={mockData}
        initialData={appointmentData}
        onSubmit={(data) => handleUpdateAppointment(bookingData.id, data)}
        onCancel={closeSlideout}
        isEditing={true}
      />
    );

    openSlideout('Edit Appointment', formContent, 'large');
  };

  const handleUpdateAppointment = async (appointmentId, appointmentData) => {
    try {
      // Update the appointments data structure
      const updatedAppointments = { ...appointments };

      // Find and update the appointment
      Object.keys(updatedAppointments).forEach(dateKey => {
        const dayAppointments = updatedAppointments[dateKey];
        const appointmentIndex = dayAppointments.findIndex(apt => apt.id === appointmentId);

        if (appointmentIndex !== -1) {
          // Update the appointment with new data
          updatedAppointments[dateKey][appointmentIndex] = {
            ...updatedAppointments[dateKey][appointmentIndex],
            patient: appointmentData.patientName,
            practitioner: appointmentData.practitioner,
            type: appointmentData.service,
            time: appointmentData.time,
            location: appointmentData.location === 'Harley Street' ? 'harley-street' :
                      appointmentData.location === 'Canary Wharf' ? 'canary-wharf' :
                      appointmentData.location === 'City of London' ? 'city-london' : 'harley-street'
          };
        }
      });

      setAppointments(updatedAppointments);
      closeSlideout();
      showNotification('Appointment updated successfully!', 'success');
    } catch (error) {
      showNotification('Error updating appointment. Please try again.', 'error');
    }
  };

  const handleDayClick = (dateStr) => {
    setSelectedDate(dateStr);
    setView('day');
    const dayAppointments = appointments[dateStr] || [];
    showNotification(`Viewing ${dayAppointments.length} appointment(s) on ${new Date(dateStr).toLocaleDateString()}`, 'info');
  };

  const handleBackToMonth = () => {
    setView('month');
    setSelectedDate(null);
  };

  const getFilteredAppointments = (dateStr) => {
    const dayAppointments = appointments[dateStr] || [];
    
    return dayAppointments.filter(appointment => {
      if (filters.practitioner && !appointment.practitioner.toLowerCase().includes(filters.practitioner.toLowerCase())) {
        return false;
      }
      if (filters.location && appointment.location !== filters.location) {
        return false;
      }
      return true;
    });
  };

  // Destructure slideout state for easier access
  const { isOpen, title, content, width } = slideoutState;

  // Calculate quick stats
  const calculateQuickStats = () => {
    const today = new Date().toISOString().split('T')[0];
    const todayAppointments = appointments[today] || [];

    // Calculate total appointments for the month
    const currentMonth = currentDate.getMonth();
    const currentYear = currentDate.getFullYear();
    let monthlyAppointments = 0;

    Object.keys(appointments).forEach(dateStr => {
      const date = new Date(dateStr);
      if (date.getMonth() === currentMonth && date.getFullYear() === currentYear) {
        monthlyAppointments += appointments[dateStr].length;
      }
    });

    // Calculate utilization rate (rough estimate based on available slots)
    const workingDaysInMonth = 22; // Average working days
    const slotsPerDay = 8; // Average slots per day
    const totalSlots = workingDaysInMonth * slotsPerDay;
    const utilizationRate = Math.round((monthlyAppointments / totalSlots) * 100);

    return {
      todayAppointments: todayAppointments.length,
      monthlyAppointments,
      utilizationRate: Math.min(utilizationRate, 100) // Cap at 100%
    };
  };

  const quickStats = calculateQuickStats();

  return (
    <div className="calendar-view">
      <PageHeader pageKey="calendar">
        <button
          className="btn btn-secondary"
          onClick={() => showNotification('Calendar export started', 'info')}
        >
          <span className="icon icon--export icon--default"></span>
          Export
        </button>
      </PageHeader>

      {/* Quick Stats Cards */}
      <div className="calendar-quick-stats">
        <div className="stat-card stat-card-modern">
          <div className="stat-content">
            <div className="stat-number">{quickStats.todayAppointments}</div>
            <div className="stat-label">Today's Appointments</div>
          </div>
          <div className="stat-indicator stat-indicator-primary"></div>
        </div>
        <div className="stat-card stat-card-modern">
          <div className="stat-content">
            <div className="stat-number">{quickStats.monthlyAppointments}</div>
            <div className="stat-label">This Month</div>
          </div>
          <div className="stat-indicator stat-indicator-success"></div>
        </div>
        <div className="stat-card stat-card-modern">
          <div className="stat-content">
            <div className="stat-number">{quickStats.utilizationRate}%</div>
            <div className="stat-label">Utilization Rate</div>
          </div>
          <div className="stat-indicator stat-indicator-warning"></div>
        </div>
      </div>

      {/* Premium Calendar Card */}
      <div className="calendar-card-premium">
        <CalendarControls
          mockData={mockData}
          currentDate={currentDate}
          view={view}
          filters={filters}
          onMonthChange={handleMonthChange}
          onViewChange={handleViewChange}
          onFiltersChange={handleFiltersChange}
          onAddAppointment={handleAddAppointment}
        />

        <CalendarGrid
          currentDate={currentDate}
          view={view}
          selectedDate={selectedDate}
          appointments={appointments}
          filters={filters}
          onAppointmentClick={handleAppointmentClick}
          onDayClick={handleDayClick}
          onBackToMonth={handleBackToMonth}
          getFilteredAppointments={getFilteredAppointments}
        />
      </div>

      {/* Floating Action Button */}
      <button
        className="floating-action-btn"
        onClick={handleAddAppointment}
        title="New Appointment"
      >
        <span className="fab-icon">+</span>
      </button>

      <SlideoutPanel
        isOpen={isOpen}
        onClose={closeSlideout}
        title={title}
        width={width}
      >
        {content}
      </SlideoutPanel>
    </div>
  );
};

// Booking Detail Component (copied from BookingsView)
const BookingDetail = ({ booking, onEdit }) => {
  const formatDateTime = (datetime) => {
    const date = new Date(datetime);
    const dateStr = date.toLocaleDateString('en-GB', {
      weekday: 'long',
      day: 'numeric',
      month: 'long',
      year: 'numeric'
    });
    const timeStr = date.toLocaleTimeString('en-GB', {
      hour: '2-digit',
      minute: '2-digit'
    });
    return { dateStr, timeStr };
  };

  const { dateStr, timeStr } = formatDateTime(booking.datetime);

  return (
    <div className="booking-detail">
      <div className="detail-sections">
        {/* Basic Info */}
        <div className="detail-section">
          <h4>Booking Information</h4>
          <div className="detail-grid">
            <div className="detail-item">
              <label>Booking ID</label>
              <span>{booking.id}</span>
            </div>
            <div className="detail-item">
              <label>Status</label>
              <span className={`status-badge status-${booking.status}`}>
                {booking.status}
              </span>
            </div>
          </div>
        </div>

        {/* Patient & Practitioner */}
        <div className="detail-section">
          <h4>Patient & Practitioner</h4>
          <div className="detail-grid">
            <div className="detail-item">
              <label>Patient Name</label>
              <span>{booking.patient}</span>
            </div>
            <div className="detail-item">
              <label>Practitioner</label>
              <span>{booking.practitioner}</span>
            </div>
          </div>
        </div>

        {/* Appointment Details */}
        <div className="detail-section">
          <h4>Appointment Details</h4>
          <div className="detail-grid">
            <div className="detail-item">
              <label>Service</label>
              <span>{booking.service}</span>
            </div>
            <div className="detail-item">
              <label>Duration</label>
              <span>{booking.duration}</span>
            </div>
            <div className="detail-item">
              <label>Date</label>
              <span>{dateStr}</span>
            </div>
            <div className="detail-item">
              <label>Time</label>
              <span>{timeStr}</span>
            </div>
            <div className="detail-item">
              <label>Location</label>
              <span>{booking.location}</span>
            </div>
          </div>
        </div>
      </div>

      <div className="detail-actions">
        <button className="btn btn-primary" onClick={onEdit}>
          Edit Booking
        </button>
      </div>
    </div>
  );
};

export default CalendarView;